/** CATMAN-B013/B016: exhibido/no exhibido según planograma en edición, no flag de tienda del backend. */

export const _hasExhibitionViewFilter = (filters?: { exhibition?: boolean; noExhibition?: boolean }): boolean =>
  !!filters?.exhibition || !!filters?.noExhibition;

export const _getPlacedProdCodeUoms = (placedProdsItemData: any[]): Set<string> => {
  const codeUoms = (placedProdsItemData || []).map(p => p?._internalCodeUom).filter(Boolean);
  return new Set(codeUoms);
};

export const _resolvePlacedProdCodeUom = (prod: any): string | null => {
  if (!prod) return null;
  if (prod._internalCodeUom) return prod._internalCodeUom;

  const code = prod._internalCode ?? prod.code;
  const uom = prod.unitOfMeasureId;
  if (code != null && uom != null) return `${code}-${uom}`;

  return null;
};

export const _isProdInCurrentPlanogram = (prod: any, placedCodeUoms: Set<string>): boolean => {
  const codeUom = _resolvePlacedProdCodeUom(prod);
  return codeUom != null && placedCodeUoms.has(codeUom);
};

export const _applyPlanogramExhibitionToProdList = (
  prodListdata: any[],
  placedProdsItemData: any[],
  filters?: { exhibition?: boolean; noExhibition?: boolean }
): any[] => {
  if (!prodListdata?.length) return prodListdata || [];

  const placedCodeUoms = _getPlacedProdCodeUoms(placedProdsItemData);

  let items = prodListdata.map(prod => ({
    ...prod,
    hasExhibition: _isProdInCurrentPlanogram(prod, placedCodeUoms),
  }));

  if (filters?.exhibition) {
    items = items.filter(prod => prod.hasExhibition);
  } else if (filters?.noExhibition) {
    items = items.filter(prod => !prod.hasExhibition);
  }

  return items;
};

/** Contadores del panel: total scope, exhibidos en planograma y sin exhibir (RN-04). */
export const _resolvePlanogramExhibitionCounts = (
  totalItemsScope: number,
  placedProdsItemData: any[],
  filters?: { exhibition?: boolean; noExhibition?: boolean }
): { total: number; exhibited: number; noExhibited: number; displayCount: number } => {
  const total = Math.max(0, totalItemsScope ?? 0);
  const exhibited = _getPlacedProdCodeUoms(placedProdsItemData).size;
  const noExhibited = Math.max(0, total - exhibited);

  let displayCount = total;
  if (filters?.exhibition) displayCount = exhibited;
  else if (filters?.noExhibition) displayCount = noExhibited;

  return { total, exhibited, noExhibited, displayCount };
};

/**
 * CATMAN-B031: contador del panel de Productos.
 *
 * Requisito UX: el label ("Productos (N)") muestra el TOTAL manipulable tras filtros y
 * **no cambia con el scroll infinito** (el listado sigue cargando de a `pageSize`).
 *
 * Estrategia (estable durante el scroll; un solo ajuste al completar la caché):
 *
 * A) Filtros 100% frontend (vista categoría/tag, exhibido/no exhibido):
 *    - Caché completa (`cacheLength >= totalItemsScope`): `renderedCount` (exacto, ya excluye
 *      no renderizables). Fix del "exhibibles 14 / lista 0".
 *    - Caché incompleta: estimación estable que NO depende de cuántas páginas se scrollearon:
 *      · exhibido → colocados con dimensiones válidas en el canvas (`renderablePlacedCount`)
 *      · sin exhibir → `totalItemsScope - placedCount`
 *      · vista (categoría/tag) → `totalItemsScope` (sobreestimación estable hasta completar caché)
 *
 * B) Filtros que el backend ya conoce (default, búsqueda, activo/bloqueado):
 *    - Caché incompleta: `totalItemsScope` (estable; no descuenta descartes página a página).
 *    - Caché completa: `totalItemsScope - discardedByDimensionsCount`.
 *
 * Exactitud en la 1ª carga (sin scrollear ni completar caché) requiere que el backend excluya del
 * `totalItemCount` las UOM sin dimensiones válidas (Task 8, handoff BE).
 */
export interface ProdListDisplayCountParams {
  totalItemsScope: number;
  filters?: { exhibition?: boolean; noExhibition?: boolean };
  hasViewFilter?: boolean;
  renderedCount?: number;
  discardedByDimensionsCount?: number;
  /** Ítems ya descargados en `prodListdataScopeCache`. */
  cacheLength?: number;
  /** Cantidad de colocados en el planograma (con o sin packaging). */
  placedCount?: number;
  /** Colocados con dimensiones válidas (estimación estable del filtro "exhibido"). */
  renderablePlacedCount?: number;
}

/** Mismo criterio que `_convertListData`: sin w/h no se puede renderizar ni arrastrar. */
export const _prodHasRenderableDimensions = (prod: any): boolean => {
  if (!prod) return false;
  const size = prod.size || prod.itemData?.size;
  return !!(size && size.w && size.h);
};

export const _countRenderablePlacedProds = (placedProdsItemData: any[]): number =>
  (placedProdsItemData || []).filter(_prodHasRenderableDimensions).length;

export const _isProdListScopeCacheComplete = (cacheLength: number, totalItemsScope: number): boolean => {
  const scope = Math.max(0, totalItemsScope ?? 0);
  if (scope <= 0) return (cacheLength ?? 0) > 0;
  return (cacheLength ?? 0) >= scope;
};

export const _resolveProdListDisplayCount = (params: ProdListDisplayCountParams): number => {
  const {
    totalItemsScope = 0,
    filters,
    hasViewFilter = false,
    renderedCount = 0,
    discardedByDimensionsCount = 0,
    cacheLength = 0,
    placedCount = 0,
    renderablePlacedCount = 0,
  } = params || ({} as ProdListDisplayCountParams);

  const scope = Math.max(0, totalItemsScope ?? 0);
  const cacheComplete = _isProdListScopeCacheComplete(cacheLength, scope);
  const isFrontendOnlyFilter = hasViewFilter || !!filters?.exhibition || !!filters?.noExhibition;

  // (A) Filtros 100% frontend
  if (isFrontendOnlyFilter) {
    // Exacto y estable una vez cargado todo el alcance.
    if (cacheComplete) {
      return Math.max(0, renderedCount ?? 0);
    }

    // Estimaciones estables (no crecen/bajan con cada página del scroll).
    if (filters?.exhibition) {
      return Math.max(0, renderablePlacedCount ?? 0);
    }
    if (filters?.noExhibition) {
      return Math.max(0, scope - (placedCount ?? 0));
    }
    // Vista categoría/tag: sobreestimación estable hasta completar caché en background.
    return scope;
  }

  // (B) Total del backend. No descontar descartes página a página (eso hace bajar el número
  // al scrollear). Solo al completar la caché se ajusta restando no renderizables detectados.
  if (cacheComplete) {
    return Math.max(0, scope - (discardedByDimensionsCount ?? 0));
  }
  return scope;
};

/** Asegura que productos removidos del canvas sigan disponibles para el filtro "sin exhibir". */
export const _ensureProdsInScopeCache = (cache: any[], itemDataList: any[]): any[] => {
  const next = [...(cache || [])];
  const existing = new Set(next.map(p => _resolvePlacedProdCodeUom(p)).filter(Boolean));

  for (const itemData of itemDataList || []) {
    const codeUom = _resolvePlacedProdCodeUom(itemData);
    if (codeUom && !existing.has(codeUom)) {
      next.push(itemData);
      existing.add(codeUom);
    }
  }

  return next;
};
